import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import getUuidByString from 'uuid-by-string';
import { PAY_WITH_CASH, isFeatureEnabled } from 'utilities/features';
import PageLoader from 'typescript/components/page_loader';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { getCountryCode, getLocaleString } from 'typescript/utilities/locales';
import {
  initCashAppPay,
  cashAppPayParams,
  createPaymentRequestPayload,
  usePaymentSdk,
} from 'typescript/utilities/web_payment_sdk';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { CHECKOUT_ROUTES } from 'typescript/constants/enums/checkout';
import { useSubmitOrder } from 'typescript/apps/checkout_v3/components/payment/use_submit_order';
import { PaymentType } from 'typescript/constants/enums/payment';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';

const CashAppRedirect = () => {
  const history = useHistory();
  const scriptLoaded = usePaymentSdk();
  const { completeOrderError } = useCompleteOrderContext();
  const { orderNumber, applicationId, locationId } = useCheckoutFlow();

  const {
    orderSummaryData,
    isPending: orderDataIsPending,
    error: orderDataError,
  } = useGetOrderSummary({ orderNumber });

  const { submitOrder } = useSubmitOrder(orderNumber);

  if (orderDataError) {
    history.push(CHECKOUT_ROUTES.DELIVERY);
  }

  if (orderDataIsPending) {
    return <PageLoader />;
  }

  const validateReferenceId = (referenceId: string): boolean => {
    const uuid = getUuidByString(orderNumber + orderSummaryData.total.amount);
    return uuid === referenceId;
  };

  useEffect(() => {
    if (orderSummaryData) {
      if (
        !isFeatureEnabled(PAY_WITH_CASH) ||
        orderSummaryData.payment_due.payment_schedule === 'installment'
      ) {
        history.push(CHECKOUT_ROUTES.DELIVERY);
      }
    }
  }, [orderSummaryData]);

  useEffect(() => {
    if (!orderSummaryData || !scriptLoaded) return;

    const payments = window.Square.payments(applicationId, locationId);
    payments.setLocale(getLocaleString());

    initCashAppPay(
      payments,
      createPaymentRequestPayload(orderSummaryData, getCountryCode()),
      cashAppPayParams(window.location.pathname, 'pass'),
    ).then((cashAppPay) => {
      if (!cashAppPay) {
        // TODO (bdang) : Migrate exisitng utils to redirect while maintining params
        history.push(CHECKOUT_ROUTES.DELIVERY, { error: 'cash_app_pay' });
        return;
      }

      cashAppPay.addEventListener('ontokenization', (event: CustomEvent) => {
        const { tokenResult } = event.detail;
        const referenceId = tokenResult?.details?.cashAppPay?.referenceId;

        if (tokenResult && validateReferenceId(referenceId)) {
          submitOrder({
            paymentMethod: cashAppPay,
            paymentType: PaymentType.CASH_APP_PAY,
            sqPayments: payments,
            cashAppTokenResult: tokenResult,
          });
        } else {
          history.push(CHECKOUT_ROUTES.DELIVERY, { error: 'cash_app_pay' });
        }
      });
    });
  }, [orderSummaryData, scriptLoaded]);

  // Redirect to payment page if there is an error in completing order.
  useEffect(() => {
    if (completeOrderError) {
      history.push(CHECKOUT_ROUTES.PAYMENT, { error: 'cash_app_pay' });
    }
  }, [completeOrderError]);

  return <PageLoader />;
};

export default CashAppRedirect;
